import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import TableComponent from 'components/partials/table-component/table-component';
import Loader from 'components/partials/loader/loader';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import { getGoalCriticalElementsTableSettings } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-tabs/goal-critical-elements-table-settings';
import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

import { getClientGoalCriticalElements } from 'api/generated/client-goal-critical-elements/client-goal-critical-elements';
import type { CriticalElementDto } from 'api/generated/schemas';

import Icons from 'assets/icons';

interface CriticalElementsTabProps {
  data: Goal & { enteringClassYear?: number };
  clientId: string | number | undefined;
  userId: string | undefined;
}

const CriticalElementsTab = ({
  data,
  clientId,
  userId,
}: CriticalElementsTabProps): React.JSX.Element => {
  const [criticalElements, setCriticalElements] = useState<CriticalElementDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCriticalElements = async (): Promise<void> => {
      if (!data?.id || !clientId || !userId) return;

      setLoading(true);
      try {
        const list = await getClientGoalCriticalElements(
          userId,
          Number(data.id),
        );

        setCriticalElements(list ?? []);
      } catch (error) {
        console.error('Error fetching linked critical elements:', error);
        setCriticalElements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCriticalElements();
  }, [data?.id, userId]);

  const handleViewDetails = (criticalElementId: string | number): void => {
    const year = data.enteringClassYear;

    navigate(
      `/admin/clients/${clientId}/critical-elements/${year}/${criticalElementId}`
    );
  };

  const columns = getGoalCriticalElementsTableSettings(handleViewDetails);

  if (!criticalElements?.length && !loading) {
    return (
      <EmptySpaceContent
        image={<Icons.EmptyImage customClass="w-[120px] h-[120px]" />}
        text="No linked Critical Elements found"
      />
    );
  }

  return (
    <>
      <Loader
        loading={loading}
        overlay="white"
        customClass="opacity-75"
        fullScreen={false}
      />

      <TableComponent
        columns={columns}
        data={criticalElements}
        isLoading={loading}
        customHeight="max-h-[50vh]"
        pagination={false}
      />
    </>
  );
};

export default CriticalElementsTab;
